import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./TablePicker.css";

const ITEM_H = 44;          // one wheel row, px (keep in sync with TablePicker.css --tp-item)
const PLACEHOLDER = "Select table";

const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The iOS-style picker sheet: a wheel of the cafe's tables with a highlight band, Cancel / Done.
 * Nothing is chosen until Done, so scrolling past tables never changes the order.
 */
function WheelSheet({ tables, value, onDone, onCancel }) {
  // With nothing chosen yet, the wheel opens on a "Select table" row so that Done can't pick Table 1 by accident.
  const rows = value ? tables : [{ number: "", name: PLACEHOLDER }, ...tables];
  const startIndex = Math.max(0, rows.findIndex((r) => r.number === value));
  const [index, setIndex] = useState(startIndex);
  const listRef = useRef(null);
  const frame = useRef(0);
  const doneRef = useRef(null);
  const cancelRef = useRef(null);

  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = startIndex * ITEM_H;
    listRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const onScroll = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = listRef.current;
      if (!el) return;
      const next = Math.min(rows.length - 1, Math.max(0, Math.round(el.scrollTop / ITEM_H)));
      setIndex((current) => (current === next ? current : next));
    });
  }, [rows.length]);

  const goTo = useCallback((i, smooth = true) => {
    const next = Math.min(rows.length - 1, Math.max(0, i));
    listRef.current?.scrollTo({ top: next * ITEM_H, behavior: smooth ? "smooth" : "auto" });
    setIndex(next);
  }, [rows.length]);

  const done = () => onDone(rows[index]?.number || "");

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel(); return; }
    if (e.key === "Tab") {
      // Keep focus inside the sheet: Cancel -> wheel -> Done -> Cancel.
      const order = [cancelRef.current, listRef.current, doneRef.current];
      const at = order.indexOf(document.activeElement);
      const next = order[(at + (e.shiftKey ? order.length - 1 : 1)) % order.length];
      e.preventDefault();
      next?.focus();
      return;
    }
    if (document.activeElement !== listRef.current) return;
    if (e.key === "ArrowDown") { e.preventDefault(); goTo(index + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); goTo(index - 1); }
    else if (e.key === "Home") { e.preventDefault(); goTo(0); }
    else if (e.key === "End") { e.preventDefault(); goTo(rows.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); done(); }
  };

  return createPortal(
    <div className="tp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }} onKeyDown={onKeyDown}>
      <div className="tp-sheet" role="dialog" aria-modal="true" aria-label="Select your table">
        <div className="tp-bar">
          <button ref={cancelRef} type="button" className="tp-bar-btn" onClick={onCancel}>Cancel</button>
          <span className="tp-bar-title">Your table</span>
          <button ref={doneRef} type="button" className="tp-bar-btn tp-bar-done" onClick={done}>Done</button>
        </div>
        <div className="tp-wheel">
          <span className="tp-band" aria-hidden="true" />
          <div
            ref={listRef}
            className="tp-list"
            role="listbox"
            tabIndex={0}
            aria-label="Tables"
            aria-activedescendant={`tp-opt-${index}`}
            onScroll={onScroll}
          >
            {rows.map((row, i) => {
              const distance = Math.min(3, Math.abs(i - index));
              return (
                <div
                  key={row.number || "placeholder"}
                  id={`tp-opt-${i}`}
                  role="option"
                  aria-selected={i === index}
                  className={`tp-item d${distance} ${row.number ? "" : "tp-placeholder"}`}
                  onClick={() => goTo(i)}
                >
                  {row.name}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * The cart's table step: the cafe's own tables behind one tap, shown like an iPhone picker — a field that
 * opens a wheel in a bottom sheet. `tables` is [{ number, name }]; `value` is the chosen table number as a
 * string. `onSelect` gets the whole table ({ number, name }): the cart keeps both, the name being what the POS shows.
 */
export default function TablePicker({ status, tables, value, onSelect, onRetry, invalid }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    // The sheet is gone from the DOM next frame; hand focus back to the field.
    requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }, []);

  if (status === "error") {
    return (
      <div className="table-picker-state" role="alert">
        <p className="table-picker-state-text">Couldn't load the tables. Check your connection and try again.</p>
        <button type="button" className="table-picker-retry" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  if (status !== "ready") {
    return <span className="table-picker-field table-picker-skeleton" role="status" aria-label="Loading tables" />;
  }

  const chosen = tables.find((t) => t.number === value);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`table-picker-field ${invalid ? "invalid" : ""} ${chosen ? "has-value" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className="table-picker-field-text">{chosen ? chosen.name : "Choose your table"}</span>
        <Chevron />
      </button>
      {open && (
        <WheelSheet
          tables={tables}
          value={value}
          onCancel={close}
          onDone={(number) => { const picked = tables.find((t) => t.number === number); if (picked) onSelect(picked); close(); }}
        />
      )}
    </>
  );
}
